import { useEffect, useState } from "react";

export type HashScreen = "login" | "principal";

export interface ResolvedHashScreen {
  readonly hash: "#login" | "#principal";
  readonly screen: HashScreen;
}

export function normalizeHashScreen(hash: string): HashScreen {
  return hash === "#principal" ? "principal" : "login";
}

export function resolveHashScreen(hash: string, hasValidBearer: boolean): ResolvedHashScreen {
  if (hash === "#principal" && hasValidBearer) {
    return { hash: "#principal", screen: "principal" };
  }
  return { hash: "#login", screen: "login" };
}

export function readHashScreen(): HashScreen {
  const screen = normalizeHashScreen(window.location.hash);
  const normalizedHash = `#${screen}`;
  if (window.location.hash !== normalizedHash) {
    window.history.replaceState(null, "", normalizedHash);
  }
  return screen;
}

export function setHashScreen(screen: HashScreen): void {
  const hash = `#${screen}`;
  if (window.location.hash !== hash) {
    window.location.hash = hash;
  }
}

export function useHashScreen(beforeLogin: () => void): HashScreen {
  const [screen, setScreen] = useState<HashScreen>(() => readHashScreen());

  useEffect(() => {
    const onHashChange = (): void => {
      const nextScreen = readHashScreen();
      if (nextScreen === "login") {
        beforeLogin();
      }
      setScreen(nextScreen);
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, [beforeLogin]);

  return screen;
}
